import { useCallback, useEffect, useRef, useState } from 'react';
import type { ComponentType } from 'react';
import { renderPreviewStillDataUrl } from '@/lib/render/renderPreviewStillDataUrl';
import { withStillCorsCacheKey } from '@/lib/cover/withStillCorsCacheKey';
import { QuoteSlide } from '@/remotion/carousel/QuoteSlide';
import type { QuoteSlideProps } from '@/lib/quoteCarousel/buildQuoteSlideProps';

export type SlideRenderStatus = {
  index: number;
  status: 'pending' | 'rendering' | 'done' | 'failed';
};

type StillFormat = 'png' | 'jpeg';

export const QUOTE_SLIDE_COMPOSITION_ID = 'quote-slide';

function renderSlide(props: QuoteSlideProps, format: StillFormat, signal: AbortSignal) {
  return renderPreviewStillDataUrl<QuoteSlideProps>({
    component: QuoteSlide as ComponentType<QuoteSlideProps>,
    inputProps: withStillCorsCacheKey(props),
    width: props.width,
    height: props.height,
    fps: 30,
    durationInFrames: 1,
    frame: 0,
    format,
    compositionId: QUOTE_SLIDE_COMPOSITION_ID,
    signal,
  });
}

/**
 * Renders quote slides to still images in the browser, one at a time (the
 * web renderer allows a single render at once). Shared by the ZIP download
 * and the publish dialog: `renderAll` returns one data URL per slide (null =
 * that slide failed or was cancelled) and never throws; a failed slide does
 * not stop the rest. `retry` re-renders a single slide with the props and
 * format of the last `renderAll`.
 */
export function useCarouselExport() {
  const [progress, setProgress] = useState<SlideRenderStatus[]>([]);
  const [running, setRunning] = useState(false);
  const lastRunRef = useRef<{ propsList: QuoteSlideProps[]; format: StillFormat } | null>(null);
  const controllerRef = useRef<AbortController | null>(null);
  const runningRef = useRef(false);

  const setStatus = useCallback((index: number, status: SlideRenderStatus['status']) => {
    setProgress((prev) => prev.map((entry) => (entry.index === index ? { ...entry, status } : entry)));
  }, []);

  const renderAll = useCallback(
    async (propsList: QuoteSlideProps[], format: StillFormat): Promise<Array<string | null>> => {
      controllerRef.current?.abort();
      const controller = new AbortController();
      controllerRef.current = controller;
      lastRunRef.current = { propsList, format };
      runningRef.current = true;
      setRunning(true);
      setProgress(propsList.map((_, index) => ({ index, status: 'pending' })));

      const results: Array<string | null> = propsList.map(() => null);
      try {
        for (let index = 0; index < propsList.length; index += 1) {
          if (controller.signal.aborted) break;
          setStatus(index, 'rendering');
          try {
            results[index] = await renderSlide(propsList[index], format, controller.signal);
            setStatus(index, 'done');
          } catch {
            setStatus(index, controller.signal.aborted ? 'pending' : 'failed');
          }
        }
      } finally {
        if (controllerRef.current === controller) {
          controllerRef.current = null;
          runningRef.current = false;
          setRunning(false);
        }
      }
      return results;
    },
    [setStatus],
  );

  const retry = useCallback(
    async (index: number): Promise<string | null> => {
      const last = lastRunRef.current;
      const props = last?.propsList[index];
      if (!last || !props || runningRef.current) return null;
      const controller = new AbortController();
      controllerRef.current = controller;
      runningRef.current = true;
      setRunning(true);
      setStatus(index, 'rendering');
      try {
        const url = await renderSlide(props, last.format, controller.signal);
        setStatus(index, 'done');
        return url;
      } catch {
        setStatus(index, controller.signal.aborted ? 'pending' : 'failed');
        return null;
      } finally {
        if (controllerRef.current === controller) {
          controllerRef.current = null;
          runningRef.current = false;
          setRunning(false);
        }
      }
    },
    [setStatus],
  );

  const cancel = useCallback(() => {
    controllerRef.current?.abort();
  }, []);

  // Single-slide renders ("Baixar imagem") keep their own controllers so
  // they never cancel, or show up in, a running ZIP export.
  const singleControllersRef = useRef(new Set<AbortController>());
  const renderOne = useCallback(
    async (props: QuoteSlideProps, format: StillFormat): Promise<string | null> => {
      const controller = new AbortController();
      singleControllersRef.current.add(controller);
      try {
        return await renderSlide(props, format, controller.signal);
      } catch {
        return null;
      } finally {
        singleControllersRef.current.delete(controller);
      }
    },
    [],
  );

  // Never leave a render running for an unmounted page.
  useEffect(() => {
    const singles = singleControllersRef.current;
    return () => {
      controllerRef.current?.abort();
      singles.forEach((controller) => controller.abort());
    };
  }, []);

  return {
    progress, running, renderAll, retry, cancel, renderOne,
  };
}
